'use client';

import nds from '@nerdwallet/base-styles/classes';
import { Button, Type } from '@nerdwallet/currency';
import BarChart from '@nerdwallet/react-icon/gizmo/Barchart';
import Layers from '@nerdwallet/react-icon/gizmo/Layers';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import Write from '@nerdwallet/react-icon/gizmo/Write';
import CreateTemplate from '@nerdwallet/react-icon/material/Apps';
import ContentCopy from '@nerdwallet/react-icon/material/ContentCopy';
import classNames from 'classnames';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { useSearchParams } from 'next/navigation';
import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  alwaysRenderInBuilder,
  createTemplate,
  deleteComponent,
  openEditor,
  openPageMap,
  selectComponent,
  setComponentType,
  startSorting,
  stopSorting,
  toggleBadge,
} from '~/app/lib/redux/actions/builder';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import * as componentLibrary from '../../lib/component-lib';
import ActionDropdown from './action-dropdown';
import Dropzone from './dropzone';
import { NewOfferIdModal } from './new-offer-id-modal';

import './editor-control.scss';

// Components that should not move their badge when selected.
const aboveBlacklist = {
  Page: true,
  GlobalNav: true,
};

const nonSortableComponents = new Set(['Page']);

const childrenToRender = ({
  children,
  node,
  outlineLength,
  watchQueryParams,
  dispatch,
  componentsToAlwaysRenderInBuilder,
  forceRenderInBuilder,
  handleAddForceRenderInBuilder,
}) => {
  if (forceRenderInBuilder) {
    return children;
  }

  if (
    outlineLength < 40 ||
    watchQueryParams ||
    includes(componentsToAlwaysRenderInBuilder, node.id)
  ) {
    return children;
  }

  // if the component defines its own JSX to render when it doesn't show in the builder, display that instead of the default
  const alternativeNode = get(
    componentLibrary,
    `${node.type}.alternativeBuilderDisplay`
  );
  const childToRender = alternativeNode ? (
    alternativeNode(dispatch, node)
  ) : (
    <Type
      size={2}
      className={nds('padding-all-4', 'text-align-center')}
      onClick={() => dispatch(alwaysRenderInBuilder(node.id))}
    >
      This contains too may components to display in the builder. Click to load
      me! <p className={nds('padding-vertical-3')}>Or</p>
      <Button onClick={handleAddForceRenderInBuilder}>
        Render all the components in the builder
      </Button>
    </Type>
  );

  return childToRender;
};

const isValidSibling = (reorderingId, targetId, flatOutline) => {
  const componentType = get(flatOutline, [targetId, 'type']);

  if (nonSortableComponents.has(componentType)) {
    return '';
  }

  let foundId = targetId;

  while (foundId) {
    foundId = get(flatOutline, [foundId, 'parentId']);

    if (foundId === reorderingId) {
      return '';
    }
  }

  return true;
};

const forceRenderParamName = 'forceRenderInBuilder';

const EditorControl = ({ children, node }) => {
  const editorControlRef = useRef(null);
  const actionDropdownRef = useRef(null);
  const dispatch = useDispatch();
  const { selected } = useBuilder();
  const searchParams = useSearchParams();
  const forceRenderInBuilder = searchParams.get(forceRenderParamName);
  const [showNewOfferIdInput, setShowNewOfferIdInput] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const {
    flatOutline,
    watchQueryParams,
    badges,
    alwaysRenderInBuilder: componentsToAlwaysRenderInBuilder,
  } = useBuilder();
  const outlineLength = Object.keys(flatOutline).length;

  const sortingComponentId = useSelector((state) => {
    const sortingId = state.builder.sorting.componentId;
    const targetId = node.id;
    return (
      sortingId &&
      isValidSibling(sortingId, targetId, state.builder.flatOutline) &&
      sortingId
    );
  });
  const badgeOpen = badges[node.id];

  const baseButton = {
    size: 'small',
  };

  const isSelected = selected === node.id;
  const nodeType = node?.type?.toLowerCase();

  const selectAndOpenEditor = useCallback(
    (nodeId, componentType, abTesting = false) => {
      dispatch(setComponentType('root'));
      dispatch(selectComponent(nodeId));
      dispatch(openEditor(abTesting));
    },
    [dispatch]
  );

  const editProps = {
    ...baseButton,
    className: 'editor-control__button editor-control__edit',
    title: 'Edit Component',
    onClick: () => {
      // select component
      selectAndOpenEditor(node.id, node.type);
      dispatch(openPageMap());
    },
  };

  const deleteProps = {
    ...baseButton,
    className: 'editor-control__button editor-control__delete',
    title: 'Delete Component',
    onClick() {
      dispatch(deleteComponent(node.id));
    },
  };

  const ABTestingProps = {
    ...baseButton,
    className: 'editor-control__button',
    title: 'A/B Testing',
    onClick: () => {
      // select component
      selectAndOpenEditor(node.id, node.type, true);
      dispatch(openPageMap());
    },
  };

  const createTemplateProps = {
    ...baseButton,
    className: 'editor-control__button',
    title: 'Copy Component',
    onClick() {
      setShowNewOfferIdInput(true);
    },
  };

  const cloneTemplateProps = {
    ...baseButton,
    className: 'editor-control__button',
    title: 'Clone Layout',
    onClick() {
      dispatch(createTemplate(node.id, null));
    },
  };

  const componentName = node.type;

  const toggleTitle = `${
    badgeOpen ? 'Close' : 'Open'
  } ${componentName} details.`;

  const shouldBeAboveComponent = isSelected && !aboveBlacklist[componentName];

  const isSortable = !nonSortableComponents.has(node.type);

  const moveProps = isSortable
    ? {
        title: 'Move this component',
        draggable: true,
        onDragStart(evt) {
          evt.dataTransfer.effectAllowed = 'move';
          setTimeout(() => dispatch(startSorting(node.id)), 100);
        },
        onDragEnd() {
          // send a deferred update in case the user drops the component
          // outside a valid dropzone
          setTimeout(() => dispatch(stopSorting), 100);
        },
      }
    : {};

  const showABtestingBadge =
    node?.props?.ABTesting?.name && node?.props?.ABTesting?.enabled;

  const handleAddForceRenderInBuilder = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(forceRenderParamName)) {
      url.searchParams.append(forceRenderParamName, true);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const showCreateTemplateButton =
    componentName === 'DynamicGrid' &&
    node?.props?.options?.marketplaceOfferIds;

  const displayComponentName =
    componentName === 'DynamicGrid' ? 'Layout' : componentName;
  const ActionDropdownLeftPosition = editorControlRef.current?.clientWidth - 3;

  return (
    <div
      id={node.id}
      key={node.id}
      className={classNames({
        'editor-control': true,
        'editor-control__selected': isSelected,
        'editor-control__reorder-moving': sortingComponentId === node.id,
      })}
    >
      <NewOfferIdModal
        modalOpen={showNewOfferIdInput}
        toggleOpen={() => setShowNewOfferIdInput(false)}
        nodeId={node.id}
      />
      <div
        ref={editorControlRef}
        className={classNames(
          'editor-control__type',
          `editor-control__type__${nodeType}`,
          { 'editor-control__type--menu-open': isActionMenuOpen }
        )}
        style={{
          opacity: badgeOpen ? 1 : 0.4,
          marginTop: shouldBeAboveComponent ? -35 : 0,
        }}
        {...moveProps}
      >
        <Layers
          className="editor-control__logo editor-control__toggle"
          onClick={() => dispatch(toggleBadge(node.id))}
          title={toggleTitle}
        />
        <span
          className="editor-control__details"
          style={{
            display: badgeOpen ? 'inline-flex' : 'none',
          }}
        >
          <strong>{displayComponentName}</strong>
          <a {...editProps}>
            <Write />
          </a>
          <ActionDropdown
            ref={actionDropdownRef}
            menuStyle={{
              left: `calc(100% - ${ActionDropdownLeftPosition}px)`,
            }}
            actions={[
              {
                key: 'clone',
                label: 'Clone Layout',
                icon: <ContentCopy />,
                onClick: cloneTemplateProps.onClick,
                title: 'Clone Layout',
                show: componentName === 'DynamicGrid',
              },
              {
                key: 'clone-with-new-offer',
                label: 'Clone With New Offer',
                icon: <CreateTemplate />,
                onClick: createTemplateProps.onClick,
                title: 'Clone With New Offer',
                show: showCreateTemplateButton,
              },
              {
                key: 'ab-testing',
                label: 'A/B Testing',
                icon: <BarChart />,
                onClick: ABTestingProps.onClick,
                title: 'A/B Testing',
                show: node.type !== 'Page',
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <Trash />,
                onClick: deleteProps.onClick,
                title: 'Delete',
                danger: true,
                show: node.type !== 'Page',
              },
            ]}
            isSelected={isSelected}
            onOpenChange={setIsActionMenuOpen}
          />
          {showABtestingBadge && (
            <div className="editor-control__ab-testing-name-badge">
              {node?.props?.ABTesting?.name}
            </div>
          )}
        </span>
      </div>

      {sortingComponentId !== node.id ? (
        <Dropzone
          targetId={node.id}
          name={componentName}
          sortingComponentId={sortingComponentId}
          dispatch={dispatch}
        />
      ) : null}

      {childrenToRender({
        children,
        node,
        outlineLength,
        watchQueryParams,
        dispatch,
        componentsToAlwaysRenderInBuilder,
        forceRenderInBuilder,
        handleAddForceRenderInBuilder,
      })}
    </div>
  );
};

EditorControl.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
  dispatch: PropTypes.func.isRequired,
  selected: PropTypes.string,
  node: PropTypes.object,
  outlineLength: PropTypes.number,
  badgeOpen: PropTypes.bool,
  sortingComponentId: PropTypes.string,
  history: PropTypes.object,
  watchQueryParams: PropTypes.bool,
  componentsToAlwaysRenderInBuilder: PropTypes.arrayOf(PropTypes.string),
};

EditorControl.displayName = 'EditorControl';

export default EditorControl;
